import { POINT_RULE_ACTION_LABELS, PointRuleActionType } from "./pointRule";

/**
 * Movimientos del ledger de puntos en el BO (GET /bo/points/ledger). Tipos camelCase del DTO.
 *
 * La pantalla existe para responder "a quién se le descontaron puntos y por qué". El caso real
 * es la penalidad por inasistencia, pero los canjes también descuentan, así que el recorte NO
 * es por acción sino por SIGNO: `reversal` y `refund` son positivos (devuelven puntos) y una
 * lista armada con `action_type IN ('no_show', 'redeem', 'reversal')` mostraría devoluciones
 * como si fueran pérdidas.
 */

/**
 * Entradas que no salen de una regla: las pone el sistema. Espeja
 * `POINT_LEDGER_SYSTEM_ACTION_TYPES` de `worker-api/src/shared/constants/points.ts`.
 */
export type PointLedgerSystemActionType =
  | "redeem"
  | "refund"
  | "reversal"
  | "adjustment"
  | "expiration";

export type PointLedgerActionType = PointRuleActionType | PointLedgerSystemActionType;

/** Entidad a la que apunta el movimiento (`related_entity_type` del ledger). */
export type PointLedgerRelatedType =
  | "event"
  | "user"
  | "suggestion"
  | "redemption"
  | "window"
  | "ledger";

/** Qué tajada pide la pantalla. `debit` = `points_awarded < 0`, o sea lo que se descontó. */
export type PointLedgerSign = "debit" | "credit" | "all";

export interface AdminLedgerEntry {
  id: number;
  /** La institución de la fila: en scope "Todos" es lo único que la atribuye. */
  tenantId: string;
  /** Instante ISO. */
  createdAt: string;
  /** El mail puede faltar: hay cuentas creadas por el BO sin mail. */
  user: { id: number; fullName: string; email: string | null };
  /**
   * Crudo, como lo manda el backend y no como unión cerrada: una acción nueva del ledger tiene
   * que poder mostrarse igual (ver `pointLedgerActionLabel`), no caer en un valor por descarte.
   */
  actionType: string;
  /** Con signo, tal cual el ledger: negativo = se descontó. */
  points: number;
  /** null en los movimientos de sistema (canje, ajuste): no hay regla que los tarife. */
  rule: { id: number; title: string | null } | null;
  /** `id` viaja como texto porque `related_entity_id` es polimórfico en la base. */
  related: { type: string; id: string; label: string | null } | null;
  /**
   * Los dos motivos del ajuste manual y quién lo cargó. `null` en todo lo demás: el motor no
   * escribe motivos, sólo una persona lo hace.
   *
   * `internalReason` es la nota de operaciones y SÓLO SE VE ACÁ. `userReason` es el texto que
   * la persona lee en su historial de la app, en lugar del genérico "Ajuste de Chispas".
   */
  adjustment: {
    internalReason: string;
    userReason: string;
    createdBy: { id: number; fullName: string };
  } | null;
  /**
   * true si otra entrada la revierte (`reverses_ledger_id`). Una penalidad por inasistencia se
   * revierte cuando el host corrige la marca: sin este dato el admin reclama puntos que ya se
   * devolvieron.
   */
  isReversed: boolean;
}

export interface PointLedgerPage {
  items: AdminLedgerEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface PointLedgerFilters {
  /** null = todas las instituciones (SUPER_ADMIN en "Todos"). */
  tenantId: string | null;
  sign: PointLedgerSign;
  actionType: PointLedgerActionType | null;
  /**
   * Lo acepta el endpoint y hoy ninguna pantalla lo manda: queda para entrar por persona desde
   * el detalle del usuario. `undefined` y `null` son lo mismo acá (no filtra).
   */
  userId?: number | null;
  /**
   * El mail EXACTO de la persona (sin distinguir mayúsculas), no una búsqueda parcial: el caso
   * de uso es pegar el mail que vino en un reclamo, y un parcial no filtraría nada útil
   * —"@grups.app" matchea a casi todos—.
   *
   * El backend encuentra también a las cuentas eliminadas por su mail ANTERIOR, que son justo
   * las que generan reclamos: sus movimientos quedan en el ledger para siempre.
   *
   * Se combina con `userId` por AND, como el resto de los filtros. `null` = no filtra, y lo
   * mismo una cadena vacía o de puros espacios: el param se arma con `trim()` y no viaja.
   */
  email: string | null;
  /** YYYY-MM-DD, inclusive. */
  from: string | null;
  to: string | null;
}

export interface PointLedgerQuery extends PointLedgerFilters {
  page: number;
  limit: number;
}

export const POINT_LEDGER_SIGN_LABELS: Record<PointLedgerSign, string> = {
  debit: "Descuentos",
  credit: "Acreditaciones",
  all: "Todos los movimientos",
};

export const POINT_LEDGER_SYSTEM_ACTION_LABELS: Record<PointLedgerSystemActionType, string> = {
  redeem: "Canje de premio",
  refund: "Devolución de canje",
  reversal: "Reversión",
  adjustment: "Ajuste manual",
  expiration: "Vencimiento de puntos",
};

/** Las acciones con regla se nombran igual que en la pantalla de Reglas. */
export const POINT_LEDGER_ACTION_LABELS: Record<PointLedgerActionType, string> = {
  ...POINT_RULE_ACTION_LABELS,
  ...POINT_LEDGER_SYSTEM_ACTION_LABELS,
};

export const POINT_LEDGER_RELATED_TYPE_LABELS: Record<PointLedgerRelatedType, string> = {
  event: "Evento",
  user: "Usuario",
  suggestion: "Sugerencia",
  redemption: "Canje",
  window: "Período",
  ledger: "Movimiento",
};

const labelOrNull = <Key extends string>(
  labels: Record<Key, string>,
  key: string
): string | null =>
  Object.prototype.hasOwnProperty.call(labels, key) ? labels[key as Key] : null;

/**
 * Etiqueta de la acción. Un `action_type` que el BO todavía no conoce se muestra crudo: es un
 * dato que el admin puede buscar, no un vacío.
 */
export const pointLedgerActionLabel = (actionType: string): string =>
  labelOrNull(POINT_LEDGER_ACTION_LABELS, actionType) ?? actionType;

export const pointLedgerRelatedTypeLabel = (relatedType: string): string =>
  labelOrNull(POINT_LEDGER_RELATED_TYPE_LABELS, relatedType) ?? relatedType;

// Signo menos tipográfico (U+2212), no el guion: mismo criterio que pointRule.ts.
const MINUS_SIGN = "−";

/** "−5 puntos" / "+10 puntos": el signo adelante, para leer la pérdida de un vistazo. */
export const formatPointLedgerPoints = (points: number): string => {
  const amount = Math.abs(points);
  const unit = amount === 1 ? "punto" : "puntos";
  return `${points < 0 ? MINUS_SIGN : "+"}${amount} ${unit}`;
};

/** Título de la regla que tarifó el movimiento; null cuando no salió de una regla. */
export const formatPointLedgerRule = (rule: AdminLedgerEntry["rule"]): string | null => {
  if (rule === null) return null;
  return rule.title ?? `Regla #${rule.id}`;
};

/**
 * A qué evento o premio corresponde. Si el backend no pudo resolver el nombre (la entidad se
 * borró, o el tipo es uno que no tiene nombre) se muestra el tipo y el id, que igual sirve
 * para rastrearlo.
 */
export const formatPointLedgerRelated = (
  related: AdminLedgerEntry["related"]
): string | null => {
  if (related === null) return null;
  return related.label ?? `${pointLedgerRelatedTypeLabel(related.type)} #${related.id}`;
};

/**
 * Alta de un movimiento manual (POST /bo/points/ledger/adjustments).
 *
 * EL SIGNO VA EN `points`, no en un campo aparte: positivo suma, negativo resta. Un
 * `operation` separado admitiría "restar −50", que suma, y que no se lee bien en la grilla.
 * El formulario sí muestra dos botones (Sumar / Restar), pero los traduce a un signo antes de
 * armar el payload: eso vive en `pointAdjustmentForm.ts`.
 */
export interface CreatePointAdjustmentPayload {
  /** Sólo viaja cuando el SUPER_ADMIN está en "Todas": al resto se lo impone el backend. */
  tenantId: string | null;
  email: string;
  points: number;
  internalReason: string;
  userReason: string;
}

export interface CreatedPointAdjustment {
  ledgerId: number;
  tenantId: string;
  user: { id: number; fullName: string; email: string | null };
  points: number;
  /** El saldo que le quedó a la persona, leído por el backend en la misma transacción. */
  balanceAfter: number;
  internalReason: string;
  userReason: string;
  createdAt: string;
}

/**
 * Una persona ofrecida por el buscador del alta.
 *
 * EL SALDO VIENE CON LA OPCIÓN: quien está por restar necesita verlo antes de elegir, porque
 * un descuento mayor al saldo lo rechaza el backend.
 */
export interface PointAdjustmentUserOption {
  id: number;
  fullName: string;
  email: string;
  balance: number;
}

/** Espejo del mínimo del backend: con menos letras el endpoint devuelve vacío igual. */
export const MIN_POINT_ADJUSTMENT_SEARCH = 2;

/** Espejo de `MAX_MANUAL_ADJUSTMENT_POINTS` del backend: tope de sanidad, no de negocio. */
export const MAX_POINT_ADJUSTMENT_POINTS = 10_000;

/** Espejos de los CHECK de `point_ledger_adjustment`. */
export const MAX_POINT_ADJUSTMENT_INTERNAL_REASON = 500;
export const MAX_POINT_ADJUSTMENT_USER_REASON = 280;
